import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/Warehouses.css';
import { API_BASE_URL } from '../../config';

function Warehouses({ email, onLogout }) {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ show: false, message: '', isError: false });

  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      // First try to fetch using POST with auth credentials
      try {
        if (authKey && userId) {
          const response = await fetch(`${API_BASE_URL}/brands/warehouses/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auth_key: authKey,
              user_id: userId
            })
          });
          
          const result = await response.json();
          
          if (response.ok) {
            console.log('Warehouse data from POST API:', result.data);
            if (Array.isArray(result.data)) {
              setWarehouses(result.data);
              setLoading(false);
              return;
            }
          }
        }
      } catch (postError) {
        console.error('POST request failed, trying GET:', postError);
      }
      
      // If POST fails or auth credentials missing, try the GET method with email
      if (email) {
        const getResponse = await fetch(`${API_BASE_URL}/brands/warehouses?email=${encodeURIComponent(email)}`);
        const getResult = await getResponse.json();
        
        if (getResponse.ok) {
          console.log('Warehouse data from GET API:', getResult.data);
          if (Array.isArray(getResult.data)) {
            setWarehouses(getResult.data);
          } else {
            console.error('GET: Warehouse data is not an array:', getResult.data);
            setWarehouses([]);
            setError('Received invalid warehouse data format');
          }
        } else {
          console.error('GET API error:', getResult);
          setError(getResult.message || 'Failed to fetch warehouses');
        }
      } else {
        setError('No email provided to fetch warehouses');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch warehouses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handleSync = async (e, warehouseId) => {
    e.stopPropagation(); // Prevent navigation to edit page
    try {
      // Get auth credentials from localStorage
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      const response = await fetch(`${API_BASE_URL}/brands/warehouses/sync/${warehouseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_key: authKey,
          user_id: userId
        })
      });

      if (response.ok) {
        setSyncStatus({
          show: true,
          message: 'Warehouse synced successfully',
          isError: false
        });
      } else if (response.status === 422) {
        setSyncStatus({
          show: true,
          message: 'This warehouse has already been synced. Updates are not allowed - please create a new warehouse address if needed.',
          isError: true
        });
      } else {
        throw new Error('Failed to sync warehouse');
      }
    } catch (error) {
      setSyncStatus({
        show: true,
        message: error.message || 'Failed to sync warehouse',
        isError: true
      });
    }

    // Hide the notification after 5 seconds instead of 3 for longer messages
    setTimeout(() => {
      setSyncStatus({ show: false, message: '', isError: false });
    }, 5000);
  };

  return (
    <>
      {loading ? (
        <div className="dashboard-loading">
          <div className="loader"></div>
          <p>Loading your warehouses...</p>
        </div>
      ) : (
        <div className="dashboard-root">
          <TopMenu pageTitle="Warehouse Locations" email={email} onLogout={onLogout} />
          <div className="dashboard-container"> 
            {error && (
              <div className="error-state">
                <p>{error}</p>
              </div>
            )}

            {syncStatus.show && (
              <div className={`sync-notification ${syncStatus.isError ? 'error' : 'success'}`}>
                {syncStatus.message}
              </div>
            )}
            
            <div className="warehouses-grid">
              {Array.isArray(warehouses) && warehouses.length > 0 ? (
                warehouses.map((warehouse) => (
                  <div 
                    key={warehouse.warehouse_id}
                    className="warehouse-card"
                    onClick={() => navigate(`/warehouses/${warehouse.warehouse_id}`)}
                  >
                    <div className="warehouse-info">
                      <div className="warehouse-header">
                        <h3>{warehouse.name}</h3>
                        <button 
                          className="sync-button"
                          onClick={(e) => handleSync(e, warehouse.warehouse_id)}
                          title="Sync warehouse"
                        >
                          ↻
                        </button>
                      </div>
                      <div className="address-details">
                        <p>{warehouse.address}</p>
                        <p>{warehouse.city}, {warehouse.state} {warehouse.zip}</p>
                        <p>{warehouse.country}</p>
                      </div>
                      <div className="status-badge">
                        {warehouse.active ? 'Active' : 'Inactive'}
                      </div>
                      <div className="warehouse-details">
                        <p>Hours: {warehouse.operationHoursStart.substring(0,2)}:{warehouse.operationHoursStart.substring(2,4)} - {warehouse.operationHoursEnd.substring(0,2)}:{warehouse.operationHoursEnd.substring(2,4)}</p>
                        <p>POC: {warehouse.pocName}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : null}

              <div 
                className="warehouse-card add-new"
                onClick={() => navigate('/warehouses/add')}
              >
                <div className="add-warehouse-content">
                  <div className="plus-icon">+</div>
                  <h3>Add New Warehouse</h3>
                  <p>Click to add a new warehouse location</p>
                </div>
              </div>
            </div>

            {!loading && (!Array.isArray(warehouses) || warehouses.length === 0) && (
              <div className="no-warehouses">
                <h2>No Warehouses Found</h2>
                <p>You haven't added any warehouse locations yet. Click the "Add New Warehouse" tile to get started.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Warehouses; 